import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ConfigCard from './ConfigCard';
import { CheckboxTile } from './_PdiHelpers';

const SECTIONS = [
    { key: 'productDetails',   i18nKey: 'cfg_pdi_open_section_product_details', fallback: 'Product & details' },
    { key: 'variants',         i18nKey: 'cfg_pdi_open_section_variants',        fallback: 'Variants'          },
    { key: 'relatedProducts',  i18nKey: 'cfg_pdi_open_section_related',         fallback: 'Related products'  },
    { key: 'articleInfo',      i18nKey: 'cfg_pdi_open_section_article_info',    fallback: 'Article info'      },
    { key: 'washInstructions', i18nKey: 'cfg_pdi_open_section_wash',            fallback: 'Wash instructions' },
    { key: 'stockByStore',     i18nKey: 'cfg_pdi_open_section_stock_store',     fallback: 'Stock by store'    },
];

// Module scope on purpose: declared inside the card this got a fresh identity
// per render, so every toggle remounted all 12 CheckboxTiles instead of
// re-rendering them.
const Column = ({ icon, label, data, device, tr, toggle }) => (
    <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            {icon}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        </Box>
        <Stack spacing={0.75}>
            {SECTIONS.map(({ key, i18nKey, fallback }) => (
                <CheckboxTile
                    key={key}
                    label={tr(i18nKey, fallback)}
                    checked={!!data[key]}
                    onChange={(v) => toggle(device, key, v)}
                />
            ))}
        </Stack>
    </Box>
);

const SectionsOpenByDefaultCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const desktop = value.desktop || {};
    const mobile  = value.mobile  || {};

    const toggle = (device, k, v) => {
        if (!onChange) return;
        onChange(device, { ...(value[device] || {}), [k]: v });
    };

    return (
        <ConfigCard
            anchor="cfg-card-pdi-open"
            title={tr('cfg_pdi_open_title', 'Sections open by default')}
            description={tr('cfg_pdi_open_desc', 'Some sections on the product page are collapsible. Pick which ones should be open when a visitor first lands on the page.')}
            theme={theme}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                <Column
                    tr={tr}
                    toggle={toggle}
                    icon={<DesktopMacIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                    label={tr('cfg_pdi_open_desktop', 'Desktop')}
                    data={desktop}
                    device="desktop"
                />
                <Column
                    tr={tr}
                    toggle={toggle}
                    icon={<SmartphoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                    label={tr('cfg_pdi_open_mobile', 'Mobile')}
                    data={mobile}
                    device="mobile"
                />
            </Box>
        </ConfigCard>
    );
};

SectionsOpenByDefaultCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default SectionsOpenByDefaultCard;
